import { useEffect, useRef } from "react";
import { useUserStore } from "../../user/stores";
import { COMPLIANCE_ENABLED } from "../config";
import { useComplianceStore } from "../stores";

/**
 * 合规数据的启动钩子：登录态一成立就取一次定位并查一次 KYC 状态。
 *
 * 【为什么挂在登录态上，而不是改登录页】
 * 1. 本分支的 /login 已经重定向到底部登录弹窗（见 AppRouter 的 LoginPopupRedirect），
 *    而各模板的登录入口形态不一（页面 / 弹窗 / 三方授权回跳），挨个去改既容易漏，
 *    也会把合规逻辑散进登录表单里；
 * 2. 服务端校验定位要带 externalId / externalEmail（就是玩家 id 和邮箱），
 *    登录前根本拿不到，校验结果也无从归属到人。
 *
 * 效果与需求一致：玩家点完登录、大厅还在 loading 时，浏览器就把定位权限框弹出来了
 * （截图里那一幕）。拒绝或判定不合规都只是把状态记下来，不在这里拦人 ——
 * 拦截发生在点游戏时（见 gate.ts），与需求描述一致。
 */
export const useComplianceBootstrap = (): void => {
  const isAuthenticated = useUserStore(s => s.isAuthenticated());
  const runGeoCheck = useComplianceStore(s => s.runGeoCheck);
  const refreshKycStatus = useComplianceStore(s => s.refreshKycStatus);
  const resetCompliance = useComplianceStore(s => s.resetCompliance);
  // 一个登录周期只跑一次：userInfo 每次刷新都会触发重渲染，
  // 不设闸门会反复要定位权限。
  const startedRef = useRef(false);

  useEffect(() => {
    if (!COMPLIANCE_ENABLED) return;

    if (!isAuthenticated) {
      // 退出登录要清掉上一个人的判定，否则换号登录会沿用前一个人的 KYC 状态
      if (startedRef.current) {
        startedRef.current = false;
        resetCompliance();
      }
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    // 两个都是 fire-and-forget：失败各自在 store 里落成兜底状态，
    // 不阻塞首屏，也不弹任何东西。
    void runGeoCheck();
    void refreshKycStatus();
  }, [isAuthenticated, runGeoCheck, refreshKycStatus, resetCompliance]);
};
